import { GlobalState } from 'ha/types/store';
import { Language } from '@ha/intl';

import { LinkData } from './types';
import transformHermesInfo from './utils/transformHermesInfo';
import transformHermesData from './utils/transformHermesData';

export const getError = (globalState: GlobalState) => globalState.hermes.error;

export const getData = (globalState: GlobalState) => globalState.hermes.data;

export const getInfo = (globalState: GlobalState) => globalState.hermes.info;

export const isCurrentPage = (globalState: GlobalState, pathname: string) => {
  const info = getInfo(globalState);

  if (!info) {
    return false;
  }
  const { alternateHrefs } = info.meta;

  return Object.keys(alternateHrefs).some(
    lang =>
      decodeURI(alternateHrefs[lang].toLowerCase()) ===
      decodeURI(pathname.toLowerCase()),
  );
};

export const getRoute = (globalState: GlobalState) => {
  const info = getInfo(globalState);

  return info && transformHermesInfo(info);
};

export const getRouteData = (globalState: GlobalState) => {
  const data = getData(globalState);
  const info = getInfo(globalState);

  return data && info && transformHermesData(data, info.type);
};

export const getAlternateHrefs = (
  globalState: GlobalState,
  pathname: string,
) => {
  const info = getInfo(globalState);

  if (!info || !isCurrentPage(globalState, pathname)) {
    return [];
  }

  const { alternateHrefs } = info.meta;

  return Object.keys(alternateHrefs).map((lang: Language) => ({
    code: lang,
    localisedLocation: alternateHrefs[lang],
  }));
};

export const getAlternateLangHref = (
  globalState: GlobalState,
  lang: Language,
) => {
  const info = getInfo(globalState);

  return info && info.meta.alternateHrefs[lang];
};

export const hasAlternateLangHref = (
  globalState: GlobalState,
  lang: Language,
  pathname: string,
) => {
  if (!isCurrentPage(globalState, pathname)) {
    return false;
  }

  return Boolean(getAlternateLangHref(globalState, lang));
};

type FormatUrl = (arg1: string) => string;

export function getLinkData(
  globalState: GlobalState,
  formatUrl?: FormatUrl,
): LinkData[] | undefined {
  const info = getInfo(globalState);

  if (!info) {
    return undefined;
  }

  const canonicalLink = {
    rel: 'canonical' as const,
    href: formatUrl ? formatUrl(info.meta.canonical) : info.meta.canonical,
  };

  let xDefault;
  if (info?.meta?.alternateHrefs) {
    let xDefaultLink = info.meta.alternateHrefs[Language.en];
    // if the page is available in one language then use that as x-default
    if (info.availableLanguages?.length === 1) {
      xDefaultLink = info.meta.alternateHrefs[info.availableLanguages[0]];
    }
    xDefault = xDefaultLink
      ? {
          rel: 'alternate' as const,
          hrefLang: 'x-default',
          href: formatUrl ? formatUrl(xDefaultLink) : xDefaultLink,
        }
      : undefined;
  }

  let alternateLinks: LinkData[] = [];
  if (['Country', 'Cities'].includes(info.type)) {
    alternateLinks = Object.keys(info.meta.alternateHrefs).map(lang => ({
      rel: 'alternate',
      hrefLang: lang,
      href: formatUrl
        ? formatUrl(info.meta.alternateHrefs[lang])
        : info.meta.alternateHrefs[lang],
    }));
  } else {
    alternateLinks = info.availableLanguages
      ? info.availableLanguages.map(lang => ({
          rel: 'alternate',
          hrefLang: lang,
          href: formatUrl
            ? formatUrl(info.meta.alternateHrefs[lang])
            : info.meta.alternateHrefs[lang],
        }))
      : [];
  }

  return [
    canonicalLink,
    ...(xDefault ? [xDefault] : []),
    ...(alternateLinks.length > 1 ? alternateLinks : []),
  ];
}
